import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, X, Send, List, BarChart2 } from "lucide-react";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  draft: "bg-muted text-foreground",
};

function StepStatsPanel({ seq, onClose }: any) {
  const { data: steps = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/sequences", seq._id, "steps", "stats"],
    queryFn: () => apiRequest("GET", `/api/email-mgmt/sequences/${seq._id}/steps/stats`) as unknown as Promise<any[]>,
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">{seq.name} — Step Breakdown</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        {isLoading ? (
          <div className="p-6 text-sm text-gray-400 text-center">Loading...</div>
        ) : (
          <div className="p-4 space-y-3">
            {steps.map((s: any) => (
              <div key={s.step} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center">{s.step}</span>
                  <span className="font-medium text-gray-900 text-sm">{s.label}</span>
                </div>
                {s.subject && <p className="text-xs text-gray-500 mb-3 ml-7">Subject: {s.subject}</p>}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 ml-7">
                  {[
                    { emoji: "📤", label: "Sent",      val: s.sent,       color: "text-blue-600" },
                    { emoji: "✅", label: "Delivered", val: s.delivered,  color: "text-green-600" },
                    { emoji: "👁",  label: "Opened",   val: s.opened,     color: "text-purple-600" },
                    { emoji: "🔗", label: "Clicked",   val: s.clicked,    color: "text-indigo-600" },
                    { emoji: "⚠️", label: "Bounced",   val: s.bounced,    color: "text-red-600" },
                    { emoji: "🚫", label: "Junk",      val: s.complained, color: "text-orange-600" },
                  ].map(({ emoji, label, val, color }) => (
                    <div key={label} className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className={`text-lg font-bold ${val > 0 ? color : "text-gray-300"}`}>{val}</div>
                      <div className="text-xs text-gray-400">{emoji} {label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatDrilldown({ seqId, eventType, label, onClose }: any) {
  const { data, isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/sequences", seqId, "stats", eventType],
    queryFn: () => apiRequest("GET", `/api/email-mgmt/sequences/${seqId}/stats/${eventType}`) as unknown as Promise<any>,
  });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl border shadow-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{label} — {data?.total || 0} users</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        {isLoading ? <div className="text-sm text-muted-foreground">Loading...</div> :
          data?.users?.length === 0 ? <div className="text-sm text-muted-foreground">No users yet.</div> :
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {data?.users?.map((u: any) => (
              <div key={u.email} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                <span className="text-blue-600">{u.email}</span>
                <span className="text-xs text-muted-foreground">{u.lastAt ? new Date(u.lastAt).toLocaleString() : "—"}</span>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function SequenceCard({ seq, onTest, onToggle, onEdit, onDelete }: any) {
  const [drilldown, setDrilldown] = useState<{eventType: string, label: string} | null>(null);
  const [showSteps, setShowSteps] = useState(false);
  const { data: stats } = useQuery({
    queryKey: ["/api/email-mgmt/sequences", seq._id, "stats"],
    queryFn: () => apiRequest("GET", `/api/email-mgmt/sequences/${seq._id}/stats`) as unknown as Promise<any>,
    staleTime: 60000,
  });

  const statTile = (emoji: string, label: string, value: number, activeColor: string, eventType?: string) => (
    <div
      className={`bg-muted/50 rounded-lg p-3 text-center ${eventType && value > 0 ? "cursor-pointer hover:bg-muted" : ""}`}
      onClick={() => eventType && value > 0 && setDrilldown({ eventType, label })}
    >
      <div className={`text-xl font-bold ${value > 0 ? activeColor : "text-muted-foreground"}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{emoji} {label}</div>
    </div>
  );

  return (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <List size={16} className="text-primary shrink-0" />
            <span className="font-semibold">{seq.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[seq.status] || STATUS_COLORS.draft}`}>{seq.status}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{seq.steps?.length || 0} step{seq.steps?.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={onTest}>Test</Button>
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowSteps(true)}><BarChart2 size={12} className="mr-1" />Details</Button>
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={onToggle}>
            {seq.status === "active" ? "Pause" : "Activate"}
          </Button>
          <Button size="sm" className="text-xs h-7" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="destructive" className="text-xs h-7" onClick={onDelete}>Delete</Button>
        </div>
      </div>
      {stats && (
        <div className="grid grid-cols-3 gap-2">
          {statTile("📤", "Sent",      stats.totalSent,  "text-blue-600")}
          {statTile("✅", "Delivered", stats.delivered,  "text-green-600",  "delivery")}
          {statTile("👁", "Opened",    stats.opened,     "text-purple-600", "open")}
          {statTile("🔗", "Clicked",   stats.clicked,    "text-indigo-600", "click")}
          {statTile("⚠️", "Bounced",   stats.bounced,    "text-red-600",    "bounce")}
          {statTile("🚫", "Junk",      stats.complained, "text-orange-600", "complaint")}
        </div>
      )}
    {drilldown && <StatDrilldown seqId={seq._id} eventType={drilldown.eventType} label={drilldown.label} onClose={() => setDrilldown(null)} />}
    {showSteps && <StepStatsPanel seq={seq} onClose={() => setShowSteps(false)} />}
    </div>
  );
}

export default function SequencesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [testModal, setTestModal] = useState<any>(null);
  const [testEmail, setTestEmail] = useState("");
  const [form, setForm] = useState<any>({ name: "", fromEmail: "", steps: [], status: "draft", distributionListId: "" });

  const { data: sequences = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/sequences"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/sequences") as unknown as Promise<any[]>,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ["/api/email-mgmt/templates"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/templates") as unknown as Promise<any[]>,
  });
  const { data: distLists = [] } = useQuery({
    queryKey: ["/api/email-mgmt/distribution"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/distribution") as unknown as Promise<any[]>,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/sequences"] });

  const openCreate = () => {
    setSelected(null);
    setForm({ name: "", fromEmail: "", steps: [], status: "draft", distributionListId: "" });
    setDialogOpen(true);
  };

  const openEdit = (s: any) => {
    setSelected(s);
    setForm({ name: s.name, fromEmail: s.fromEmail || "", steps: s.steps || [], status: s.status, distributionListId: s.distributionListId || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selected) await apiRequest("PUT", `/api/email-mgmt/sequences/${selected._id}`, form);
      else await apiRequest("POST", "/api/email-mgmt/sequences", form);
      showSuccess(selected ? "Sequence updated" : "Sequence created");
      setDialogOpen(false);
      invalidate();
    } catch { showError("Failed to save"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this sequence?")) return;
    try { await apiRequest("DELETE", `/api/email-mgmt/sequences/${id}`); showSuccess("Deleted"); invalidate(); }
    catch { showError("Failed to delete"); }
  };

  const toggleStatus = async (seq: any) => {
    const newStatus = seq.status === "active" ? "paused" : "active";
    try { await apiRequest("PUT", `/api/email-mgmt/sequences/${seq._id}`, { status: newStatus }); invalidate(); }
    catch { showError("Failed"); }
  };

  const handleTest = async () => {
    try {
      await apiRequest("POST", `/api/email-mgmt/sequences/${testModal._id}/test`, { email: testEmail });
      showSuccess(`Test sent to ${testEmail}`);
      setTestModal(null); setTestEmail("");
    } catch { showError("Failed to send test"); }
  };

  const addStep = () => setForm((p: any) => ({ ...p, steps: [...p.steps, { templateId: "", delayValue: 0, delayUnit: "hours" }] }));
  const removeStep = (i: number) => setForm((p: any) => ({ ...p, steps: p.steps.filter((_: any, idx: number) => idx !== i) }));
  const updateStep = (i: number, field: string, val: any) =>
    setForm((p: any) => ({ ...p, steps: p.steps.map((s: any, idx: number) => idx === i ? { ...s, [field]: val } : s) }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-muted-foreground text-sm">{sequences.length} sequence{sequences.length !== 1 ? "s" : ""}</p>
        <Button onClick={openCreate} size="sm" className="flex items-center gap-2"><Plus size={16} /> New Sequence</Button>
      </div>

      {isLoading ? <p className="text-center text-gray-400 py-8">Loading...</p>
        : sequences.length === 0 ? <p className="text-center text-muted-foreground py-8">No sequences yet</p>
        : (
          <div className="space-y-3">
            {sequences.map((seq: any) => (
              <SequenceCard key={seq._id} seq={seq}
                onTest={() => { setTestModal(seq); setTestEmail(""); }}
                onToggle={() => toggleStatus(seq)}
                onEdit={() => openEdit(seq)}
                onDelete={() => handleDelete(seq._id)}
              />

            ))}
          </div>
        )}

      <Dialog open={!!testModal} onOpenChange={() => setTestModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Test Sequence</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Sequence: <strong>{testModal?.name}</strong></p>
          <p className="text-xs text-gray-400 mb-2">Sends the first step immediately to your email.</p>
          <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="your@email.com" autoFocus />
          <div className="flex gap-2 justify-end mt-3">
            <Button variant="outline" onClick={() => setTestModal(null)}>Cancel</Button>
            <Button onClick={handleTest} className="flex items-center gap-2"><Send size={16} /> Send Test</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected ? "Edit" : "Create"} Sequence</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Sequence Name *</Label>
              <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Welcome Series" />
            </div>
            <div>
              <Label>Sender Email</Label>
              <Input type="email" value={form.fromEmail} onChange={e => setForm({ ...form, fromEmail: e.target.value })} className="mt-1" placeholder="support@chefaiq.com" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Email Steps</Label>
                <button type="button" onClick={addStep} className="text-xs text-primary hover:underline flex items-center gap-1"><Plus size={14} /> Add Step</button>
              </div>
              {form.steps.length === 0 && <p className="text-xs text-gray-400 text-center py-3 border rounded-lg">No steps yet.</p>}
              <div className="space-y-3">
                {form.steps.map((step: any, i: number) => (
                  <div key={i} className="border rounded-lg p-3 bg-muted/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-muted-foreground">Step {i + 1}</span>
                      <button type="button" onClick={() => removeStep(i)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                    </div>
                    <div className="space-y-2">
                      <select value={step.templateId || ""} onChange={e => updateStep(i, "templateId", e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                        <option value="">— Select template —</option>
                        {templates.map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
                      </select>
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Send after</span>
                        <Input type="number" min="0" value={step.delayValue}
                          onChange={e => updateStep(i, "delayValue", parseInt(e.target.value) || 0)}
                          className="w-20 h-8 text-sm" />
                        <select value={step.delayUnit} onChange={e => updateStep(i, "delayUnit", e.target.value)}
                          className="border rounded px-2 py-1.5 text-sm bg-background focus:outline-hidden">
                          {["minutes", "hours", "days"].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <span className="text-xs text-gray-400">{i === 0 ? "(from enrollment)" : "(after previous)"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Linked Distribution List <span className="text-gray-400 font-normal">(optional)</span></Label>
              <select value={form.distributionListId} onChange={e => setForm({ ...form, distributionListId: e.target.value })}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-hidden">
                <option value="">— None —</option>
                {distLists.map((l: any) => <option key={l._id} value={l._id}>{l.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{selected ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
